// Module to format dates and times

import {
    differenceInCalendarDays,
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInSeconds,
    differenceInYears,
    format,
    formatDistanceStrict,
    isSameDay,
    isSameYear,
} from 'date-fns';
import type { Locale } from 'date-fns/locale';
import { de, enUS, fr, frCA } from 'date-fns/locale';
import { useDatastore } from 'util/datastore';
import { t } from 'util/stdtypes';

import { colorTextPrimary } from './color';
import { UtilityText } from './text';
import { getLocaleFromLanguage, translateLabel, useClientLocale } from './translation';
/** @see See [Localization Docs](../../docs/contributing/localization.md) for instructions. */

/** assigns the correct date-fns locale to the language */
export const localeMap: Record<string, Locale> = {
    'de-DE': de,
    'fr-CA': frCA,
    'fr-FR': fr,
    'en-US': enUS,
};

/** returns the date format for the given date-fns locale */
function _getLocaleDateFormat(dateFnsLocale: Locale, { showYear }: { showYear?: boolean } = { showYear: false }): string {
    switch (dateFnsLocale) {
        case enUS:
            return showYear ? "MMM d',' yyyy" : 'MMM d';
        default:
            return showYear ? 'd MMM yyyy' : 'd MMM';
    }
}

/** returns the time format for the given date-fns locale */
function _getLocaleTimeOfDayFormat(dateFnsLocale: Locale): string {
    switch (dateFnsLocale) {
        case enUS:
            return 'h:mmaaa';
        case de:
            return "HH:mm 'Uhr'";
        case frCA:
        case fr:
            return "H 'h' mm";
        default:
            return 'h:mmaaa';
    }
}

function _prepareDatesFromMs(timestamp: number | undefined) {
    if (timestamp === undefined || isNaN(timestamp)) {
        return null;
    }
    const currentDate = new Date();
    const inputDate = new Date(timestamp);
    return { currentDate, inputDate };
}

export function fromNow(timestamp: number | undefined, language = 'english'): string {
    const dates = _prepareDatesFromMs(timestamp);
    if (!dates) {
        return '';
    }
    const { currentDate, inputDate } = dates;

    const { dateFnsLocale } = getLocaleFromLanguage(language);

    const diffSeconds = differenceInSeconds(currentDate, inputDate);
    if (diffSeconds < 60) {
        return translateLabel({ label: t('Just now'), language });
    }

    if (isSameDay(currentDate, inputDate)) {
        return formatDistanceStrict(inputDate, currentDate, { addSuffix: true, locale: dateFnsLocale });
    }

    const isYesterday = differenceInCalendarDays(currentDate, inputDate) === 1;
    if (isYesterday) {
        const time = format(inputDate, _getLocaleTimeOfDayFormat(dateFnsLocale), { locale: dateFnsLocale });
        return translateLabel({ label: t('Yesterday at {time}'), language, formatParams: { time } });
    }

    if (isSameYear(currentDate, inputDate)) {
        return format(inputDate, _getLocaleDateFormat(dateFnsLocale), { locale: dateFnsLocale });
    }

    return format(inputDate, _getLocaleDateFormat(dateFnsLocale, { showYear: true }), { locale: dateFnsLocale });
}

export function fromNowCompact(timestamp: number | undefined, language = 'english') {
    const dates = _prepareDatesFromMs(timestamp);
    if (!dates) {
        return '';
    }
    const { currentDate, inputDate } = dates;

    const diffInYears = differenceInYears(currentDate, inputDate);
    if (diffInYears > 0) {
        return translateLabel({ label: t('{years}y'), language, formatParams: { years: diffInYears } });
    }

    const diffInMonths = differenceInMonths(currentDate, inputDate);
    if (diffInMonths > 0) {
        return translateLabel({ label: t('{months}mo'), language, formatParams: { months: diffInMonths } });
    }

    const diffInDays = differenceInDays(currentDate, inputDate);
    if (diffInDays > 0) {
        return translateLabel({ label: t('{days}d'), language, formatParams: { days: diffInDays } });
    }

    const diffInHours = differenceInHours(currentDate, inputDate);
    if (diffInHours > 0) {
        return translateLabel({ label: t('{hours}h'), language, formatParams: { hours: diffInHours } });
    }

    const diffInMinutes = differenceInMinutes(currentDate, inputDate);
    if (diffInMinutes > 0) {
        return translateLabel({ label: t('{minutes}m'), language, formatParams: { minutes: diffInMinutes } });
    }

    return translateLabel({ label: t('now'), language });
}

export type DateTimeDisplayProps = {
    timestamp: number;
    color?: string;
    type?: string;
};

export function DateTimeDisplay({ timestamp, color = colorTextPrimary, type = 'small' }: DateTimeDisplayProps) {
    const date = new Date(timestamp);
    const datastore = useDatastore();
    const resolvedLanguage = datastore.getLanguage() || 'english';
    const { dateFnsLocale } = getLocaleFromLanguage(resolvedLanguage);

    const dateTimeFormat = _getLocaleDateFormat(dateFnsLocale, { showYear: true }) + ' | ' + _getLocaleTimeOfDayFormat(dateFnsLocale);
    const text = format(date, dateTimeFormat, { locale: dateFnsLocale });

    return <UtilityText color={color} text={text} type={type} />;
}

export function DateDisplay({ timestamp, color = colorTextPrimary, type = 'small' }: DateTimeDisplayProps) {
    const date = new Date(timestamp);
    const datastore = useDatastore();
    const resolvedLanguage = datastore.getLanguage() || 'english';
    const { dateFnsLocale } = getLocaleFromLanguage(resolvedLanguage);

    const dateTimeFormat = _getLocaleDateFormat(dateFnsLocale, { showYear: true });
    const text = format(date, dateTimeFormat, { locale: dateFnsLocale });

    return <UtilityText color={color} text={text} type={type} />;
}

function _formatDay({ day, locale }: { day: number; locale: string }) {
    if (locale === 'fr-CA' && day === 1) {
        return `${day}er`;
    }
    return day;
}

export function formatToDateRangeString({
    start = new Date(Date.UTC(2024, 5, 4, 4, 0, 0)),
    end = new Date(Date.UTC(2024, 5, 20, 4, 0, 0)),
    locale = 'fr-CA',
}): string {
    const options: Intl.DateTimeFormatOptions = {
        month: 'long',
    };
    const startDay = start.getDate();
    const endDay = end.getDate();
    const endYear = end.getFullYear();
    const isSameMonth = start.getMonth() === end.getMonth();

    switch (locale) {
        case 'de-DE':
            // Format to either '10. - 15. Mai 2024' if the range is in the same month, or '10. Mai - 15. Juni 2024' if the range is over multiple months
            return `${_formatDay({ day: startDay, locale })}.${!isSameMonth ? ` ${start.toLocaleDateString(locale, options)}` : ''} - ${_formatDay({ day: endDay, locale })}. ${end.toLocaleDateString(locale, options)} ${endYear}`;
        case 'fr-CA':
            // Format to either '10 - 15 mai' if the range is in the same month, or '10 mai - 15 juin' if the range is over multiple months
            return `${_formatDay({ day: startDay, locale })}${!isSameMonth ? ` ${start.toLocaleDateString(locale, options)}` : ''} - ${_formatDay({ day: endDay, locale })} ${end.toLocaleDateString(locale, options)} ${endYear}`;
        default:
            // Format to either 'May 10 - 15' if the range is in the same month, or 'May 10 - June 15' if the range is over multiple months
            return `${start.toLocaleDateString(locale, options)} ${_formatDay({ day: startDay, locale })} - ${!isSameMonth ? `${end.toLocaleDateString(locale, options)} ` : ''}${_formatDay({ day: endDay, locale })}, ${endYear}`;
    }
}

export type MonthAbbreviationProps = { date: Date; locale?: string };

export function MonthAbbreviation({ date, locale }: MonthAbbreviationProps) {
    const clientLocale = useClientLocale();
    const resolvedLocaleKey = locale || clientLocale;
    const resolvedLocale = localeMap[resolvedLocaleKey] || enUS;

    return <UtilityText text={format(date, 'MMM', { locale: resolvedLocale })} />;
}
